import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import * as utilsList from '../__index.utils'

export const STEP2FIELDS = {
	buildingBatchNumber: 'buildingBatchNumber',
	isSameLocation: 'isSameLocation',
	isLocationOwned: 'isLocationOwned',
	ownerFirstName: 'ownerFirstName',
	ownerLastName: 'ownerLastName',
	ownerEmail: 'ownerEmail',
	ownerPhoneNumber: 'ownerPhoneNumber'
} as const

export type Step2Fields = keyof typeof STEP2FIELDS

/**
 * useStep2State - Custom hook to manage the state for step 2 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 2.
 */

export function useStep2State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const radioListYesNo = utilsList.radioListYesNo(pageAssets)

	return {
		buildingBatchNumber: {
			name: 'buildingBatchNumber',
			label: '',
			labelKey: '',
			value: answers?.buildingBatchNumber || '',
			required: false,
			ref: useRef<HTMLSelectElement>(null)
		},
		isSameLocation: {
			name: 'isSameLocation',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_isSameLocation,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_isSameLocation',
			value: answers?.isSameLocation?.toString() || '',
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		// Numéro(s) de local (ou locaux), si applicable
		localNumber: {
			name: 'localNumber',
			label: pageAssets?.request_form_commercialPermit_localNumber,
			labelKey: 'request_form_commercialPermit_localNumber',
			value: answers?.localNumber || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		isLocationOwned: {
			name: 'isLocationOwned',
			label: pageAssets?.request_form_swimmingPoolSpaPermit_isLocationOwned,
			labelKey: 'request_form_swimmingPoolSpaPermit_isLocationOwned',
			value: answers?.isLocationOwned?.toString() || '',
			required: true,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		//****** Owner inputs ******
		ownerFirstName: {
			name: 'ownerFirstName',
			label: pageAssets?.label_firstName,
			labelKey: 'label_firstName',
			value: answers.ownerFirstName || '',
			required: answers?.isLocationOwned?.toString() == radioListYesNo[1].value,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		ownerLastName: {
			name: 'ownerLastName',
			label: pageAssets?.label_lastName,
			labelKey: 'label_lastName',
			value: answers.ownerLastName || '',
			required: answers?.isLocationOwned?.toString() == radioListYesNo[1].value,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		ownerEmail: {
			name: 'ownerEmail',
			label: pageAssets?.label_email,
			labelKey: 'label_email',
			value: answers.ownerEmail || '',
			required: answers?.isLocationOwned?.toString() == radioListYesNo[1].value,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		},
		ownerPhoneNumber: {
			name: 'ownerPhoneNumber',
			label: pageAssets?.label_telephone,
			labelKey: 'label_telephone',
			value: answers.ownerPhoneNumber || '',
			required: answers?.isLocationOwned?.toString() == radioListYesNo[1].value,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_2
		}
    }
}